import autoTable from "jspdf-autotable";
import { PDF_COLORS, PDF_FONTS, PDF_LAYOUT } from "../../utils/pdfStyles";
import { asBool, asCheckText, asText } from "../../utils/pdfHelpers";

const VALUE_LABELS = {
  presentacion: {
    CEFALICO: "Cefálico",
    PODALICO: "Podálico / Nalgas",
  },
  posicion: {
    DORSO_DERECHO: "Dorso Derecho",
    DORSO_IZQUIERDO: "Dorso Izquierdo",
  },
  situacion: {
    LONGITUDINAL: "Longitudinal",
    TRANSVERSO: "Transverso",
    OBLICUO: "Oblicuo",
  },
  descenso: {
    "GRADO_-3": "Grado -3 (Móvil / Alta)",
    GRADO_0: "Grado 0 (Encajado)",
    "GRADO_+3": "Grado +3 (En Canal)",
  },
  metodo_elegido: {
    POMEROY: "Esterilización Quirúrgica (Pomeroy)",
    IMPLANTE: "Implante Subdérmico (3, 4 o 5 años)",
    DIU: "Dispositivo Intrauterino (DIU)",
    AMPOLLA_TRIMESTRAL: "Inyección Trimestral",
    AMPOLLA_MENSUAL: "Inyección Mensual",
    NO_DESEA: "No desea planificar en el momento (Disentimiento)",
  },
  via_parto_sugerida: {
    VAGINAL: "Vía Vaginal",
    CESAREA: "Cesárea Programada",
  },
  pref_analgesia: {
    AINES: "No narcóticos / Manejo con AINES",
    NARCOTICOS: "Analgesia Sistémica / Narcóticos",
    NEURO_AXIAL: "Analgesia Neuroaxial (Epidural Temprana)",
  },
};

const formatValue = (field, val) => {
  if (val === null || val === undefined || val === "") return "-";
  const map = VALUE_LABELS[field];
  if (map && Object.prototype.hasOwnProperty.call(map, String(val))) {
    return map[String(val)];
  }
  return asText(val, "-");
};

/**
 * Dibuja una tabla de sección clínica con cabecera integrada y soporte auto-wrap
 */
const drawClinicalTable = (doc, startY, title, bodyRows, colSpan = 4, columnStyles = {}) => {
  autoTable(doc, {
    startY: startY,
    margin: { left: PDF_LAYOUT.marginX, right: PDF_LAYOUT.marginX, bottom: PDF_LAYOUT.marginBottom },
    theme: "plain",
    tableWidth: PDF_LAYOUT.contentWidth,
    head: [
      [
        {
          content: title.toUpperCase(),
          colSpan: colSpan,
          styles: {
            fillColor: PDF_COLORS.bgLight,
            textColor: PDF_COLORS.primaryDark,
            fontStyle: "bold",
            fontSize: 9,
            cellPadding: { top: 3.5, bottom: 3.5, left: 6, right: 6 },
            lineColor: PDF_COLORS.border,
            lineWidth: { top: 0.5, right: 0.5, bottom: 0.5, left: 3 },
          },
        },
      ],
    ],
    body: bodyRows,
    columnStyles: columnStyles,
    styles: {
      font: PDF_FONTS.family,
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      lineColor: PDF_COLORS.borderLight,
      lineWidth: 0.5,
      overflow: "linebreak", // Permite que los textos largos hagan salto de línea sin truncarse
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.bgLight,
    },
  });

  return doc.lastAutoTable.finalY + 5;
};

/**
 * Dibuja un checklist en 2 columnas con badges [ SÍ ] y [ NO ]
 */
const drawChecklistSection = (doc, startY, title, items) => {
  const tableBody = [];
  for (let i = 0; i < items.length; i += 2) {
    const itemA = items[i];
    const itemB = items[i + 1] || null;

    const row = [
      {
        content: asBool(itemA?.checked) ? "[ SÍ ]" : "[ NO ]",
        styles: {
          fontStyle: "bold",
          textColor: asBool(itemA?.checked) ? PDF_COLORS.success : PDF_COLORS.textMuted,
          halign: "center",
          cellWidth: 32,
        },
      },
      {
        content: asText(itemA?.label, ""),
        styles: {
          textColor: asBool(itemA?.checked) ? PDF_COLORS.textDark : PDF_COLORS.textMuted,
          fontStyle: asBool(itemA?.checked) ? "bold" : "normal",
          cellWidth: 228,
        },
      },
      {
        content: itemB ? (asBool(itemB.checked) ? "[ SÍ ]" : "[ NO ]") : "",
        styles: {
          fontStyle: "bold",
          textColor: itemB && asBool(itemB.checked) ? PDF_COLORS.success : PDF_COLORS.textMuted,
          halign: "center",
          cellWidth: 32,
        },
      },
      {
        content: itemB ? asText(itemB.label, "") : "",
        styles: {
          textColor: itemB && asBool(itemB.checked) ? PDF_COLORS.textDark : PDF_COLORS.textMuted,
          fontStyle: itemB && asBool(itemB.checked) ? "bold" : "normal",
          cellWidth: 231,
        },
      },
    ];
    tableBody.push(row);
  }

  return drawClinicalTable(doc, startY, title, tableBody, 4);
};

export const renderPlanParto = (doc, { plan, epsTramite, startY }) => {
  let y = startY;

  // 2. Parámetros Obstétricos y Control Prenatal
  y = drawClinicalTable(
    doc,
    y,
    "2. Parámetros Obstétricos y Control Prenatal",
    [
      [
        { content: "FUM:" },
        { content: asText(plan?.fum, "-") },
        { content: "Sem. Gestación:" },
        { content: plan?.semanas_gestacion ? `${plan.semanas_gestacion} sem` : "-" },
        { content: "FPP:" },
        { content: asText(plan?.fpp, "-") },
        { content: "Ingreso CPN:" },
        { content: asText(plan?.fecha_ingreso_cpn, "-") },
      ],
      [
        { content: "Controles:" },
        { content: plan?.num_controles ? String(plan.num_controles) : "-" },
        { content: "Grupo Sang.:" },
        { content: asText(plan?.grupo_sanguineo, "-") },
        { content: "Factor RH:" },
        { content: asText(plan?.rh, "-") },
        { content: "Peso Actual:" },
        { content: plan?.peso_kg ? `${plan.peso_kg} Kg` : "-" },
      ],
      [
        { content: "Alergias Conocidas:", colSpan: 2, styles: { fontStyle: "bold" } },
        { content: asText(plan?.alergias, "Niega alergias medicamentosas o alimentarias"), colSpan: 6 },
      ],
    ],
    8,
    {
      0: { cellWidth: 55, fontStyle: "bold", textColor: PDF_COLORS.textDark },
      1: { cellWidth: 70, textColor: PDF_COLORS.textMuted },
      2: { cellWidth: 78, fontStyle: "bold", textColor: PDF_COLORS.textDark },
      3: { cellWidth: 65, textColor: PDF_COLORS.textMuted },
      4: { cellWidth: 55, fontStyle: "bold", textColor: PDF_COLORS.textDark },
      5: { cellWidth: 68, textColor: PDF_COLORS.textMuted },
      6: { cellWidth: 67, fontStyle: "bold", textColor: PDF_COLORS.textDark },
      7: { cellWidth: 65, textColor: PDF_COLORS.textMuted },
    }
  );

  // 3. Examen Físico / Maniobras de Leopold (Organizado en 2 filas x 2 columnas para no truncar textos)
  y = drawClinicalTable(
    doc,
    y,
    "3. Examen Físico / Maniobras de Leopold",
    [
      [
        { content: "Presentación Fetal:", styles: { fontStyle: "bold", cellWidth: 100 } },
        { content: formatValue("presentacion", plan?.presentacion), styles: { cellWidth: 160 } },
        { content: "Posición Fetal:", styles: { fontStyle: "bold", cellWidth: 95 } },
        { content: formatValue("posicion", plan?.posicion), styles: { cellWidth: "auto" } },
      ],
      [
        { content: "Situación Fetal:", styles: { fontStyle: "bold", cellWidth: 100 } },
        { content: formatValue("situacion", plan?.situacion), styles: { cellWidth: 160 } },
        { content: "Grado de Descenso:", styles: { fontStyle: "bold", cellWidth: 95 } },
        { content: formatValue("descenso", plan?.descenso), styles: { cellWidth: "auto" } },
      ],
    ],
    4
  );

  // 4. Asesoría Anticonceptiva Postparto
  y = drawClinicalTable(
    doc,
    y,
    "4. Asesoría Anticonceptiva Postparto (Criterios Médicos)",
    [
      [
        { content: "Asesoría Recibida:", styles: { fontStyle: "bold", cellWidth: 110 } },
        { content: asCheckText(plan?.asesoria_recibida), styles: { cellWidth: 80 } },
        { content: "Método de Planificación Elegido:", styles: { fontStyle: "bold", cellWidth: 155 } },
        { content: formatValue("metodo_elegido", plan?.metodo_elegido), styles: { cellWidth: "auto" } },
      ],
    ],
    4
  );

  // 5. Red de Prestadores Asignada para Parto y Urgencias (Soporte multilínea sin puntos suspensivos)
  const prestadorParto = plan?.prestador_parto_nombre
    ? `${plan.prestador_parto_nombre} (${plan.prestador_parto_normal})`
    : asText(plan?.prestador_parto_normal, "No asignado");
  const prestadorUrg = plan?.prestador_urgencias_nombre
    ? `${plan.prestador_urgencias_nombre} (${plan.prestador_urgencias_cesarea})`
    : asText(plan?.prestador_urgencias_cesarea, "No asignado");

  y = drawClinicalTable(
    doc,
    y,
    "5. Red de Prestadores Asignada para Parto y Urgencias",
    [
      [
        { content: "IPS para Parto / Cesárea:", styles: { fontStyle: "bold", cellWidth: 145 } },
        { content: prestadorParto, styles: { cellWidth: "auto" } },
      ],
      [
        { content: "IPS para Urgencias Obstétricas:", styles: { fontStyle: "bold", cellWidth: 145 } },
        { content: prestadorUrg, styles: { cellWidth: "auto" } },
      ],
    ],
    2
  );

  // 6. Logística, Red de Apoyo Domiciliario y Solicitudes EPS
  y = drawClinicalTable(
    doc,
    y,
    "6. Logística, Red de Apoyo Domiciliario y Solicitudes EPS",
    [
      [
        { content: "Apoyo en el Hogar:", styles: { fontStyle: "bold", cellWidth: 110 } },
        { content: asCheckText(plan?.apoyo_hogar), styles: { cellWidth: 70 } },
        { content: "Nombre Familiar / Amigo de Apoyo:", styles: { fontStyle: "bold", cellWidth: 150 } },
        { content: asText(plan?.nombre_apoyo_hogar, "No registrado"), styles: { cellWidth: "auto" } },
      ],
    ],
    4
  );
  y = drawChecklistSection(doc, y, "Solicitudes Específicas Requeridas a la EPS", [
    { label: "Solicitud traslado de gestante de alto riesgo (ARO)", checked: plan?.solicitud_eps_traslado_aro },
    { label: "Solicitud traslado para cesárea programada", checked: plan?.solicitud_eps_traslado_cesarea },
    { label: "Estancia previa al parto (Zona rural dispersa)", checked: plan?.solicitud_eps_estancia_previa_rural },
    { label: "Gestante en riesgo por condiciones geográficas / zona", checked: plan?.solicitud_eps_riesgo_zona },
    { label: "Solicitud de albergue temporal / Casa materna", checked: plan?.solicitud_eps_albergue },
    { label: "Solicitud de apoyo logístico para acompañante", checked: plan?.solicitud_eps_acompanante_logistico },
  ]);

  // 7. Interconsultas de Especialidad y Enfoque Intercultural
  y = drawClinicalTable(
    doc,
    y,
    "7. Interconsultas de Especialidad y Enfoque Intercultural",
    [
      [
        { content: "Interconsulta para Vía de Parto:", styles: { fontStyle: "bold", cellWidth: 150 } },
        { content: asText(plan?.interconsulta_especialidad, "Ninguna requerida"), styles: { cellWidth: 230 } },
        { content: "Prioridad Máxima:", styles: { fontStyle: "bold", cellWidth: 85 } },
        { content: plan?.prioridad_dias ? `${plan.prioridad_dias} días` : "-", styles: { cellWidth: "auto" } },
      ],
      [
        { content: "Atención con Médico Tradicional:", styles: { fontStyle: "bold" } },
        { content: asCheckText(plan?.medico_tradicional) },
        { content: "Atención con Partera (Res. 276/19):", styles: { fontStyle: "bold" } },
        { content: asCheckText(plan?.partera) },
      ],
    ],
    4
  );

  // 8. Valoración Médica para Gestantes de Alto Riesgo (ARO)
  y = drawClinicalTable(
    doc,
    y,
    "8. Valoración Médica para Gestantes de Alto Riesgo (ARO)",
    [
      [
        { content: "Vía de Parto Recomendada:", styles: { fontStyle: "bold", cellWidth: 140 } },
        { content: formatValue("via_parto_sugerida", plan?.via_parto_sugerida), styles: { cellWidth: "auto" } },
      ],
      [
        { content: "Impresión Diagnóstica:", styles: { fontStyle: "bold", cellWidth: 140 } },
        { content: asText(plan?.impresion_diagnostica, "Sin hallazgos patológicos"), styles: { cellWidth: "auto" } },
      ],
      [
        { content: "Causales de Clasificación ARO:", styles: { fontStyle: "bold", cellWidth: 140 } },
        { content: asText(plan?.causales_aro, "Ninguna causal registrada"), styles: { cellWidth: "auto" } },
      ],
    ],
    2
  );

  // 9. Educación en Salud, Signos de Alarma y Plan de Seguridad
  y = drawChecklistSection(doc, y, "9. Educación en Salud, Signos de Alarma y Plan de Seguridad", [
    { label: "Signos neurológicos (Cefalea, fosfenos, visión borrosa)", checked: plan?.signos_alarma_neurologicos },
    { label: "Edema progresivo en cara, manos o extremidades", checked: plan?.signos_alarma_edema },
    { label: "Sangrado vaginal o pérdida de líquido amniótico", checked: plan?.signos_alarma_sangrado },
    { label: "Dolor abdominal intenso en barra / Epigastralgia", checked: plan?.signos_alarma_dolor_abdominal },
    { label: "Disminución o ausencia de movimientos fetales", checked: plan?.signos_alarma_movimientos },
    { label: "Contracciones uterinas regulares antes de fecha", checked: plan?.signos_alarma_contracciones },
    { label: "Plan seguridad: Reconoce red de urgencias y traslados", checked: plan?.plan_seguridad_red_urgencias },
    { label: "Plan seguridad: Conoce situaciones de activación inmediata", checked: plan?.plan_seguridad_situaciones_activacion },
  ]);

  // 10. Preferencias Concertadas para el Trabajo de Parto y Nacimiento
  y = drawClinicalTable(
    doc,
    y,
    "10. Preferencias Concertadas para el Trabajo de Parto y Nacimiento",
    [
      [
        { content: "Manejo de Analgesia:", styles: { fontStyle: "bold", cellWidth: 120 } },
        { content: formatValue("pref_analgesia", plan?.pref_analgesia), styles: { cellWidth: 180 } },
        { content: "Personas en Sala:", styles: { fontStyle: "bold", cellWidth: 95 } },
        { content: asText(plan?.personas_acompanantes_sala, "Acompañante de elección"), styles: { cellWidth: "auto" } },
      ],
    ],
    4
  );
  y = drawChecklistSection(doc, y, "Intervenciones y Preferencias Maternas", [
    { label: "Ambiente: Luz tenue y privacidad en sala", checked: plan?.pref_ambiente_luz_tenue },
    { label: "Acompañamiento continuo durante el trabajo de parto", checked: plan?.pref_acompanamiento_continuo },
    { label: "Libertad de movimiento y posturas de elección", checked: plan?.libertad_movimiento },
    { label: "Consentimiento informado previo a toda intervención", checked: plan?.pref_intervenciones_consentimiento },
    { label: "Evitar episiotomía o maniobra de Kristeller rutinaria", checked: plan?.pref_intervenciones_episiotomia || plan?.pref_intervenciones_kristeller },
    { label: "Contacto piel a piel inmediato con el recién nacido", checked: plan?.pref_rn_contacto_piel },
    { label: "Inicio temprano de lactancia materna precoz", checked: plan?.pref_rn_lactancia_precoz },
    { label: "Pinzamiento retrasado / oportuno del cordón umbilical", checked: plan?.pref_rn_pinzamiento_retrasado },
    { label: "Corte del cordón realizado por el acompañante", checked: plan?.pref_rn_corte_acompanante },
    { label: "Cesárea humanizada: Acompañante en quirófano", checked: plan?.pref_cesarea_acompanante_quirofano },
    { label: "Alojamiento conjunto continuo madre - hijo", checked: plan?.pref_postparto_alojamiento_conjunto },
    { label: "Vigilancia estrecha de signos de alarma en posparto", checked: plan?.pref_postparto_vigilancia_signos },
  ]);

  // 11. Trámite Administrativo y Auditoría con la EPS
  if (epsTramite && (epsTramite.eps_tramite || epsTramite.fecha_socializacion || epsTramite.profesional_eps)) {
    y = drawClinicalTable(
      doc,
      y,
      "11. Control de Trámite Administrativo y Respuesta de la EPS",
      [
        [
          { content: "EPS Tramitadora:", styles: { fontStyle: "bold", cellWidth: 100 } },
          { content: asText(epsTramite?.eps_tramite, "-"), styles: { cellWidth: 160 } },
          { content: "Fecha Socialización:", styles: { fontStyle: "bold", cellWidth: 105 } },
          { content: asText(epsTramite?.fecha_socializacion, "-"), styles: { cellWidth: "auto" } },
        ],
        [
          { content: "Profesional Auditor:", styles: { fontStyle: "bold" } },
          { content: asText(epsTramite?.profesional_eps, "-") },
          { content: "Fecha Respuesta:", styles: { fontStyle: "bold" } },
          { content: asText(epsTramite?.fecha_respuesta, "-") },
        ],
        [
          { content: "Tiempo Transcurrido:", styles: { fontStyle: "bold" } },
          { content: epsTramite?.dias_transcurridos ? `${epsTramite.dias_transcurridos} días` : "-" },
          { content: "Anexos Obligatorios:", styles: { fontStyle: "bold" } },
          {
            content: `Plan Discutido: ${asCheckText(epsTramite?.anexo_plan_parto_discutido)} | Resumen HC: ${asCheckText(epsTramite?.anexo_resumen_hc)} | Carné CLAP: ${asCheckText(epsTramite?.anexo_carnet_clap)}`,
          },
        ],
      ],
      4
    );
  }

  return y;
};
